import { useEffect, useState, type ReactNode } from 'react';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import { Alert, Box, Link as MuiLink, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { paths } from '@/routes/paths';
import { useAuthStore } from '@/stores/useAuthStore';
import { useNotificationStore } from '@/stores/useNotificationStore';
import {
  manualJobEndKeys,
  useGenerateManualJobEnd,
  useManualJobEndAssignments,
} from '@/queries/useManualJobEnd';
import { useActiveStaff } from '@/queries/useOCCerSearch';
import { useReopenJobEnd } from '@/queries/useJobEnds';
import type { StaffSearchResult } from '@/api/occerSearchApi';
import type {
  ManualJobEndAssignment,
  ManualJobEndOutcome,
} from '@/api/types/manualJobEnd.types';
import { AppraiseeSearchSection } from './AppraiseeSearchSection';
import { AssignmentsTable } from './AssignmentsTable';
import { GenerateConfirmDialog } from './GenerateConfirmDialog';
import { ReopenConfirmDialog } from './ReopenConfirmDialog';

type ActionMode = 'generate' | 'reopen';

type ResultBanner =
  | { kind: 'generated'; outcome: ManualJobEndOutcome; jobEndId: number | null }
  | { kind: 'reopened'; jobEndId: number };

const appraiseeName = (assignment: ManualJobEndAssignment) =>
  `${assignment.appraiseeFirstName} ${assignment.appraiseeLastName}`;

export function ManualJobEndPage() {
  const permissions = useAuthStore((state) => state.permissions);
  const hasAnyRole = useAuthStore((state) => state.hasAnyRole);
  const canAccess = hasAnyRole(['Admin']);
  const showNotification = useNotificationStore((state) => state.showNotification);
  const queryClient = useQueryClient();

  const [selectedStaff, setSelectedStaff] = useState<StaffSearchResult | null>(null);
  const [actionTarget, setActionTarget] = useState<ManualJobEndAssignment | null>(null);
  const [actionMode, setActionMode] = useState<ActionMode | null>(null);
  const [resultBanner, setResultBanner] = useState<ResultBanner | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Reuses the OCCer active-staff search; excludePartners drops Partner grades (never get a Job End — AC1).
  const { data: staff, isLoading: isLoadingStaff, isError: isStaffError } = useActiveStaff(true);
  const {
    data: assignments,
    isLoading: isLoadingAssignments,
    isError: isAssignmentsError,
  } = useManualJobEndAssignments(selectedStaff?.employeeId ?? null);

  const generateMutation = useGenerateManualJobEnd(selectedStaff?.employeeId ?? null);
  const reopenMutation = useReopenJobEnd();

  // Errors other than the Generate/Reopen outcome surface as the classic bottom popup
  // (never the top banner).
  useEffect(() => {
    if (isStaffError) {
      showNotification('Failed to load staff.', 'error');
    }
  }, [isStaffError, showNotification]);

  useEffect(() => {
    if (isAssignmentsError) {
      showNotification('Failed to load assignments.', 'error');
    }
  }, [isAssignmentsError, showNotification]);

  if (permissions && !canAccess) {
    return <Navigate to={paths.unauthorized} replace />;
  }

  const handleSelectStaff = (value: StaffSearchResult | null) => {
    setSelectedStaff(value);
    setResultBanner(null);
    setErrorMessage(null);
  };

  const readErrorMessage = (error: unknown, fallback: string): string => {
    if (isAxiosError(error)) {
      const message = error.response?.data?.message;
      if (typeof message === 'string' && message.trim().length > 0) {
        return message;
      }
    }
    return fallback;
  };

  const openAction = (mode: ActionMode, assignment: ManualJobEndAssignment) => {
    setErrorMessage(null);
    setResultBanner(null);
    setActionMode(mode);
    setActionTarget(assignment);
  };

  const closeDialog = () => {
    setActionMode(null);
    setActionTarget(null);
  };

  const handleConfirmGenerate = async () => {
    if (!actionTarget) return;
    try {
      const result = await generateMutation.mutateAsync({ assignmentId: actionTarget.assignmentId });
      closeDialog();
      setResultBanner({ kind: 'generated', outcome: result.outcome, jobEndId: result.jobEndId });
    } catch (error) {
      // Generate outcome → top banner only.
      closeDialog();
      setErrorMessage(readErrorMessage(error, 'Failed to generate the Job End.'));
    }
  };

  const handleConfirmReopen = async () => {
    if (!actionTarget?.jobEndId) return;
    const jobEndId = actionTarget.jobEndId;
    try {
      await reopenMutation.mutateAsync({ id: jobEndId });
      if (selectedStaff) {
        queryClient.invalidateQueries({
          queryKey: manualJobEndKeys.assignments(selectedStaff.employeeId),
        });
      }
      closeDialog();
      setResultBanner({ kind: 'reopened', jobEndId });
    } catch (error) {
      // Reopen outcome → top banner only.
      closeDialog();
      setErrorMessage(readErrorMessage(error, 'Failed to reopen the Job End.'));
    }
  };

  const pendingAssignmentId =
    generateMutation.isPending || reopenMutation.isPending
      ? (actionTarget?.assignmentId ?? null)
      : null;

  const renderResultBanner = () => {
    if (!resultBanner) return null;

    const jobEndLink =
      resultBanner.jobEndId != null ? (
        <MuiLink
          component={RouterLink}
          to={paths.jobEnd.replace(':id', String(resultBanner.jobEndId))}
        >
          view Job End
        </MuiLink>
      ) : null;

    let severity: 'success' | 'warning' = 'success';
    let content: ReactNode;

    if (resultBanner.kind === 'reopened') {
      content = jobEndLink ? (
        <>Job End successfully reopened, {jobEndLink}</>
      ) : (
        'Job End successfully reopened.'
      );
    } else if (resultBanner.outcome === 'AlreadyExists') {
      // No duplicate was created; link the Admin to the Job End that already exists.
      severity = 'warning';
      content = jobEndLink ? (
        <>A Job End already exists for this person and assignment. {jobEndLink}</>
      ) : (
        'A Job End already exists for this person and assignment.'
      );
    } else {
      // JobEndCreated — the only other outcome. Append the link when we have one.
      content = jobEndLink ? (
        <>Job End successfully generated, {jobEndLink}</>
      ) : (
        'Job End successfully generated.'
      );
    }

    return (
      <Alert severity={severity} sx={{ mb: 3 }} onClose={() => setResultBanner(null)}>
        {content}
      </Alert>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        Manual JE Generation
      </Typography>

      {renderResultBanner()}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMessage(null)} role="alert">
          {errorMessage}
        </Alert>
      )}

      <AppraiseeSearchSection
        staff={staff ?? []}
        isLoading={isLoadingStaff}
        value={selectedStaff}
        onChange={handleSelectStaff}
      />

      {selectedStaff && (
        <AssignmentsTable
          assignments={assignments ?? []}
          isLoading={isLoadingAssignments}
          onGenerate={(assignment) => openAction('generate', assignment)}
          onReopen={(assignment) => openAction('reopen', assignment)}
          pendingAssignmentId={pendingAssignmentId}
        />
      )}

      <GenerateConfirmDialog
        open={actionMode === 'generate'}
        appraiseeName={actionTarget ? appraiseeName(actionTarget) : ''}
        assignmentId={actionTarget?.assignmentId ?? ''}
        collatorName={actionTarget?.collatorName ?? ''}
        isGenerating={generateMutation.isPending}
        onClose={closeDialog}
        onConfirm={handleConfirmGenerate}
      />

      <ReopenConfirmDialog
        open={actionMode === 'reopen'}
        appraiseeName={actionTarget ? appraiseeName(actionTarget) : ''}
        assignmentId={actionTarget?.assignmentId ?? ''}
        isReopening={reopenMutation.isPending}
        onClose={closeDialog}
        onConfirm={handleConfirmReopen}
      />
    </Box>
  );
}

export default ManualJobEndPage;
